/**
 * FilterListDrawer
 *
 * Reusable filter panel presented as a drawer. Title and section headings use theme typography;
 * pill-shaped filter chips (icon + label) wrap by section. Optional footer actions use theme Buttons.
 */

import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Drawer,
  Group,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';

export interface FilterListSectionOption {
  value: string;
  label: string;
  /** Optional icon shown to the left of the label (outline style recommended). */
  icon?: React.ReactNode;
}

export interface FilterListSection {
  /** Section heading (e.g. "Status", "Outdoor & Leisure"). */
  label: string;
  options: readonly FilterListSectionOption[];
}

export interface FilterListDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** Main title in the header. Default: "Filters". */
  title?: string;
  sections: FilterListSection[];
  /**
   * Optional controlled selection per section.
   * Key = section label; value = array of option values selected.
   * If not provided, selection is internal and reported on Apply.
   */
  value?: Record<string, string[]>;
  onChange?: (sectionLabel: string, selected: string[]) => void;
  /** Called when "Apply" is clicked with current selections (section label -> selected values). */
  onApply?: (selections: Record<string, string[]>) => void;
  /** Called when "Clear all" is clicked. */
  onClear?: () => void;
  /** Show footer with Clear all, Cancel, Apply. Default: true. */
  showFooterActions?: boolean;
  /** Drawer size. Default: "md". */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  /** Drawer position. Default: "right". */
  position?: 'left' | 'right' | 'top' | 'bottom';
}

function FilterChip({
  option,
  selected,
  onToggle,
}: {
  option: FilterListSectionOption;
  selected: boolean;
  onToggle: () => void;
}) {
  const theme = useMantineTheme();
  return (
    <Button
      variant="light"
      color="gold"
      onClick={onToggle}
      leftSection={option.icon}
      size="sm"
      styles={{
        root: {
          borderRadius: 9999,
          ...(selected && {
            backgroundColor: theme.colors.gold[6],
            color: theme.white,
          }),
        },
      }}
    >
      {option.label}
    </Button>
  );
}

export function FilterListDrawer({
  opened,
  onClose,
  title = 'Filters',
  sections,
  value: controlledValue,
  onChange,
  onApply,
  onClear,
  showFooterActions = true,
  size = 'md',
  position = 'right',
}: FilterListDrawerProps) {
  const theme = useMantineTheme();
  const [internalValue, setInternalValue] = useState<Record<string, string[]>>({});

  const drawerStyles = useMemo(
    () => ({
      header: {
        marginBottom: 0,
        paddingBottom: theme.spacing.md,
        backgroundColor: theme.colors.gray[1],
      },
      title: {
        flex: 1,
      },
      content: {
        borderLeft: `1px solid ${theme.colors.gray[5]}`,
      },
    }),
    [theme]
  );

  const value = useMemo(() => {
    if (controlledValue != null) return controlledValue;
    const next: Record<string, string[]> = { ...internalValue };
    sections.forEach((s) => {
      if (next[s.label] == null) next[s.label] = [];
    });
    return next;
  }, [controlledValue, internalValue, sections]);

  const toggleOption = useCallback(
    (sectionLabel: string, optionValue: string) => {
      const current = value[sectionLabel] ?? [];
      const next = current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue];
      if (onChange) {
        onChange(sectionLabel, next);
      } else {
        setInternalValue((prev) => ({ ...prev, [sectionLabel]: next }));
      }
    },
    [onChange, value]
  );

  const handleClear = useCallback(() => {
    const empty: Record<string, string[]> = {};
    sections.forEach((s) => {
      empty[s.label] = [];
    });
    if (onChange) {
      sections.forEach((s) => onChange(s.label, []));
    } else {
      setInternalValue(empty);
    }
    onClear?.();
  }, [onChange, onClear, sections]);

  const handleApply = useCallback(() => {
    onApply?.(value);
    onClose();
  }, [onApply, onClose, value]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position={position}
      size={size}
      closeOnClickOutside
      withCloseButton={false}
      title={
        <Group justify="space-between" wrap="nowrap" style={{ width: '100%' }}>
          <Title order={3} c="gray.9">
            {title}
          </Title>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={onClose}
            aria-label="Close"
            size="md"
          >
            <IconX size={20} stroke={1.5} />
          </ActionIcon>
        </Group>
      }
      styles={drawerStyles}
    >
      <Stack gap="md" pt="xs">
        {sections.map((section, index) => {
          const selected = value[section.label] ?? [];
          return (
            <Box key={section.label}>
              {index > 0 && <Divider my="sm" />}
              <Text size="sm" fw={600} mb="xs" c="gray.9">
                {section.label}
              </Text>
              <Group gap="xs" wrap="wrap">
                {section.options.map((opt) => (
                  <FilterChip
                    key={opt.value}
                    option={opt}
                    selected={selected.includes(opt.value)}
                    onToggle={() => toggleOption(section.label, opt.value)}
                  />
                ))}
              </Group>
            </Box>
          );
        })}
      </Stack>

      {showFooterActions && (
        <Group justify="flex-end" mt="xl" gap="xs">
          <Button variant="light" color="gray" onClick={handleClear}>
            Clear all
          </Button>
          <Group gap="xs">
            <Button variant="light" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="light" onClick={handleApply}>
              Apply filters
            </Button>
          </Group>
        </Group>
      )}
    </Drawer>
  );
}
